import { useState } from 'react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarUI } from '../ui/calendar';
import { CalendarDays, X } from 'lucide-react';

/**
 * Mini-control reusable para mostrar fecha original (Alegra) + fecha modificada
 * (Flujo) con un calendario popover para editar.
 *
 * Carlos (2026-04-27): "en los listados de facturas factorizadas debe tener dos
 * fechas la fecha de pago original y la que nosotros hemos definido posteriormente.
 * Cuando se actualice desde el alegra esta fecha será la original y nosotros
 * mantendremos la fecha que nosotros modificamos en el flujo."
 *
 * Props:
 *   fechaOriginal     YYYY-MM-DD (fuente: Alegra). Read-only.
 *   fechaModificada   YYYY-MM-DD ("" = sin override, usa la original).
 *   onSet(iso)        Callback async cuando user selecciona una fecha.
 *   onClear()         Callback async para volver al original.
 *   vencida           bool. Cambia colores a rojo si está vencida y no hay override.
 *   labelOriginal     Default "Alegra".
 *   labelModificada   Default "Flujo".
 *   testId            Para data-testid base.
 */
export function FechaPagoPicker({
  fechaOriginal = '',
  fechaModificada = '',
  onSet,
  onClear,
  vencida = false,
  labelOriginal = 'Alegra',
  labelModificada = 'Flujo',
  testId = 'fecha-pago',
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const efectiva = fechaModificada || fechaOriginal;
  const tieneOverride = !!fechaModificada && fechaModificada !== fechaOriginal;

  const handleSelect = async (date) => {
    if (!date) return;
    const isoStr = date.toISOString().split('T')[0];
    if (isoStr === fechaOriginal) {
      // si elige la misma del original, limpiar override
      return handleClear();
    }
    setSaving(true);
    try {
      await onSet(isoStr);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await onClear();
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const buttonClass = tieneOverride
    ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 font-semibold'
    : vencida
    ? 'border-red-300 text-red-600 font-semibold'
    : 'border-transparent text-muted-foreground';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`text-[10px] flex items-center gap-1 px-1 rounded border hover:bg-blue-50 dark:hover:bg-blue-950/30 ${buttonClass}`}
          title={
            tieneOverride
              ? `${labelOriginal}: ${fechaOriginal}\n${labelModificada}: ${fechaModificada}\nClick para editar`
              : `${labelOriginal}: ${fechaOriginal}\nClick para definir fecha efectiva`
          }
          data-testid={testId}
        >
          <CalendarDays className="w-3 h-3" />
          {efectiva || '—'}
          {tieneOverride && <span className="text-[9px]">●</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="p-2 border-b text-xs space-y-0.5 bg-slate-50 dark:bg-slate-900">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">{labelOriginal}:</span>
            <span className="font-mono">{fechaOriginal || '—'}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">{labelModificada}:</span>
            <span className={`font-mono ${tieneOverride ? 'text-blue-700 font-semibold' : 'text-muted-foreground italic'}`}>
              {fechaModificada && tieneOverride ? fechaModificada : `(usa ${labelOriginal})`}
            </span>
          </div>
        </div>
        <CalendarUI
          mode="single"
          selected={fechaModificada ? new Date(fechaModificada + 'T00:00:00') : undefined}
          onSelect={handleSelect}
          disabled={saving}
          weekStartsOn={1}
        />
        {tieneOverride && (
          <div className="p-1 border-t">
            <Button
              size="sm"
              variant="ghost"
              className="w-full h-7 text-xs"
              onClick={handleClear}
              disabled={saving}
              data-testid={`${testId}-clear`}
            >
              <X className="w-3 h-3 mr-1" /> Volver a fecha {labelOriginal}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default FechaPagoPicker;
